import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    MakeConclusionTrendRequest,
    RateTrendRequest,
    ReferenceBookType,
    TrendApiService, TrendConfidence, TrendRate,
    TrendReason,
    TrendRelevance, TrendStatus,
} from '@npck/af-frontend-api';
import {RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {Observable, of} from 'rxjs';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {TREND_ID} from '../../../../../domain/query-params.keys';
import {Path} from '../../../../../path';
import {ANALYTIC_TREND_TAB, AnalyticsTab} from '../../../analytics.component';

@Component({
    selector: 'af-trend-make-conclusion-or-rate',
    standalone: false,
    templateUrl: './make-conclusion-or-rate-trend.component.html',
    styleUrl: './make-conclusion-or-rate-trend.component.scss',
})
export class MakeConclusionOrRateTrendComponent implements OnInit {
    ReferenceBookType = ReferenceBookType;
    tabPathToTab: Record<string, AnalyticsTab> = {trends: 'tabTrends'};
    form: FormGroup<MakeConclusionOrRateTrendForm>;
    trendId = this.activatedRoute.snapshot.paramMap.get(TREND_ID);
    isAdmin = this.authHolderService.hasAccess(['npck-admin']);

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private trendApiService: TrendApiService,
        private snackbarService: SnackbarService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
    ) {
    }

    ngOnInit(): void {
        this.buildForm();
    }

    save(): void {
        if (this.form.invalid) {
            return;
        }
        const body = {
            relevance: this.form.controls.relevance.value,
            confidence: this.form.controls.confidence.value,
            reason: this.form.controls.reason.value,
            comment: this.form.controls.comment.value,
            ...(this.isAdmin && {status: this.form.controls.status.value}),
            ...(!this.isAdmin && {rate: this.form.controls.rate.value}),
        };

        if (this.isAdmin) {
            this.trendApiService.createTrendConclusion(this.trendId, body as MakeConclusionTrendRequest)
                .pipe(rxSpinner('save'))
                .subscribe(() => {
                    this.snackbarService.showSnackBar('i18n_af_snackBarMessageTrendConclusionMade');
                    this.navigateBack();
                });
        } else {
            this.trendApiService.rateTrend(this.trendId, body as RateTrendRequest)
                .pipe(rxSpinner('save'))
                .subscribe(() => {
                    this.snackbarService.showSnackBar('i18n_af_snackBarMessageTrendRated');
                    this.navigateBack();
                });
        }
    }

    navigateBack = (): Observable<boolean> => {
        this.routerService.navigate(Path.ANALYTICS_TREND_DETAILS, {
            pathParams: {
                [TREND_ID]: this.trendId,
            },
            queryParams: {
                [TAB]: ANALYTIC_TREND_TAB,
            }
        });
        return of(false);
    };

    private buildForm(): void {
        this.form = this.formBuilder.group({
            relevance: new FormControl(null, Validators.required),
            confidence: new FormControl(null),
            reason: new FormControl(null),
            comment: new FormControl(null),
            ...(this.isAdmin && {status: new FormControl(null, Validators.required)}),
            ...(!this.isAdmin && {rate: new FormControl(null, Validators.required)}),
        });
    }
}

interface MakeConclusionOrRateTrendForm {
    reason: FormControl<TrendReason>;
    relevance: FormControl<TrendRelevance>;
    confidence: FormControl<TrendConfidence>;
    comment: FormControl<string>;
    status: FormControl<TrendStatus>;
    rate: FormControl<TrendRate>;
}
